import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhWarning";
import "../button/wow-button";

import { styles } from "./media-access-panel.css";

export type MediaAccessPanelKind = "permission-denied" | "no-device" | "device-busy" | "poor-connection";

const MEDIA_ACCESS_PANEL_KINDS: ReadonlySet<MediaAccessPanelKind> = new Set<MediaAccessPanelKind>([
  "permission-denied",
  "no-device",
  "device-busy",
  "poor-connection",
]);

export function isMediaAccessPanelKind(value: unknown): value is MediaAccessPanelKind {
  return typeof value === "string" && MEDIA_ACCESS_PANEL_KINDS.has(value as MediaAccessPanelKind);
}

export interface MediaAccessPanelTranslations {
  permissionDeniedHeading: string;
  permissionDeniedBody: string;
  noDeviceHeading: string;
  noDeviceBody: string;
  deviceBusyHeading: string;
  deviceBusyBody: string;
  poorConnectionHeading: string;
  poorConnectionBody: string;
  retry: string;
  close: string;
}

@customElement("media-access-panel")
export class MediaAccessPanel extends LitElement {
  static override styles = [styles];

  @property({ type: String })
  kind: MediaAccessPanelKind = "permission-denied";

  @property({ type: Boolean, attribute: "is-retrying" })
  isRetrying = false;

  @property({ attribute: false })
  translations?: MediaAccessPanelTranslations;

  private handleRetry = (): void => {
    this.dispatchEvent(new CustomEvent("retry", { bubbles: true, composed: true }));
  };

  private handleClose = (): void => {
    this.dispatchEvent(new CustomEvent("dismiss", { bubbles: true, composed: true }));
  };

  private getCopy(t: MediaAccessPanelTranslations): { heading: string; body: string } {
    switch (this.kind) {
      case "no-device":
        return { heading: t.noDeviceHeading, body: t.noDeviceBody };
      case "device-busy":
        return { heading: t.deviceBusyHeading, body: t.deviceBusyBody };
      case "poor-connection":
        return { heading: t.poorConnectionHeading, body: t.poorConnectionBody };
      case "permission-denied":
      default:
        return { heading: t.permissionDeniedHeading, body: t.permissionDeniedBody };
    }
  }

  override render() {
    const t = this.translations;
    if (!t) return nothing;
    const { heading, body } = this.getCopy(t);

    return html`
      <div class="panel" role="alert" aria-live="assertive">
        <div class="icon">
          <ph-warning size="28" weight="fill"></ph-warning>
        </div>
        <h3>${heading}</h3>
        <p>${body}</p>
        <div class="actions">
          <wow-button variant="primary" size="medium" ?loading=${this.isRetrying} @click=${this.handleRetry}>
            ${t.retry}
          </wow-button>
          <wow-button variant="neutral" size="medium" @click=${this.handleClose}>${t.close}</wow-button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "media-access-panel": MediaAccessPanel;
  }
}
